"use client";

import type { LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { useLayoutEffect } from "react";

import { cn } from "@/lib/utils/cn";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

export type PageLayout = "dashboard" | "list" | "detail" | "form" | "settings";
export type PageSize = "default" | "narrow";

const layoutWidth: Record<PageLayout, string> = {
  dashboard: "max-w-workspace", list: "max-w-workspace", detail: "max-w-workspace",
  form: "max-w-4xl", settings: "max-w-6xl",
};

export type WorkspacePageProps = ComponentProps<"div"> & { size?: PageSize; layout?: PageLayout; fullWidth?: boolean };

/** Canonical responsive page rail for every routed workspace view. */
export function WorkspacePage({ children, className, size = "default", layout = "detail", fullWidth = false, ...props }: WorkspacePageProps) {
  return (
    <div data-page-layout={layout} className={cn(
      "mx-auto w-full px-ds-3 pb-ds-8 pt-ds-3 sm:px-ds-4 md:pt-ds-4 lg:px-ds-5",
      !fullWidth && size === "default" && layoutWidth[layout],
      !fullWidth && size === "narrow" && "max-w-2xl",
      className,
    )} {...props}>{children}</div>
  );
}

export type PageContainerProps = Omit<WorkspacePageProps, "layout"> & { layout?: PageLayout };
export function PageContainer(props: PageContainerProps) { return <WorkspacePage {...props} />; }

export function PageStack({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex min-w-0 flex-col gap-ds-5 md:gap-ds-6", className)} {...props} />;
}

export type PageHeaderProps = {
  kicker?: string; kickerIcon?: LucideIcon; title: string; titleHref?: string; titleAdornment?: ReactNode;
  breadcrumb?: ReactNode; topRow?: ReactNode; description?: ReactNode; search?: ReactNode;
  actions?: ReactNode; meta?: ReactNode; context?: ReactNode; extra?: ReactNode; hideHeaderSubtitle?: boolean;
  heroAppend?: ReactNode; className?: string;
};

const PAGE_HEADER_SURFACE = "w-full rounded-card border border-card-border bg-surface-primary text-foreground shadow-raised";
export const pageHeaderStripClasses = PAGE_HEADER_SURFACE;
export const pageHeaderStripInnerClasses = "p-[var(--density-panel-padding)]";

/** Registers global title/search chrome and presents local page context and actions. */
export function PageHeader({ kicker, kickerIcon: KickerIcon, title, titleHref, titleAdornment, breadcrumb, topRow, description, search, actions, meta, context, extra, hideHeaderSubtitle = false, heroAppend, className }: PageHeaderProps) {
  const setWorkspaceChrome = useWorkspaceChromeStore((state) => state.setWorkspaceChrome);
  useLayoutEffect(() => {
    setWorkspaceChrome({ title, titleHref: titleHref ?? null, titleAdornment: titleAdornment ?? null, search: search ?? null, showSubtitle: !hideHeaderSubtitle });
    return () => setWorkspaceChrome({ title: "", titleHref: null, titleAdornment: null, search: null, showSubtitle: true });
  }, [hideHeaderSubtitle, search, setWorkspaceChrome, title, titleAdornment, titleHref]);

  const hasLeadingContent = Boolean(breadcrumb || kicker || description || meta || extra);
  const showSurface = Boolean(topRow || hasLeadingContent || actions || context);
  return (
    <>
      {showSurface ? (
        <header className={cn(PAGE_HEADER_SURFACE, pageHeaderStripInnerClasses, className)}>
          {breadcrumb ? <div className="mb-ds-3">{breadcrumb}</div> : null}
          {topRow ? <div className={hasLeadingContent || actions || context ? "mb-ds-3" : undefined}>{topRow}</div> : null}
          <div className="flex flex-col gap-ds-4 sm:flex-row sm:items-start sm:justify-between sm:gap-ds-6">
            {hasLeadingContent ? (
              <div className="min-w-0 flex-1 space-y-ds-2">
                {kicker ? <div className="inline-flex items-center gap-ds-2 rounded-full border border-accent/20 bg-accent/10 px-ds-3 py-ds-1 text-micro font-semibold uppercase tracking-wide text-accent">{KickerIcon ? <KickerIcon className="h-3 w-3 shrink-0" aria-hidden /> : null}{kicker}</div> : null}
                {description ? <div className="max-w-3xl text-body leading-relaxed text-muted">{description}</div> : null}
                {meta ? <div className="inline-flex max-w-full flex-wrap items-center gap-ds-2 rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2 text-micro text-muted">{meta}</div> : null}
                {extra ? <div className="max-w-4xl space-y-ds-3 pt-ds-1">{extra}</div> : null}
              </div>
            ) : null}
            {actions ? <div className="flex shrink-0 flex-wrap items-center gap-ds-2 sm:justify-end">{actions}</div> : null}
          </div>
          {context ? <div className="mt-ds-4 border-t border-card-border pt-ds-3">{context}</div> : null}
        </header>
      ) : null}
      {heroAppend ? <div className={cn(PAGE_HEADER_SURFACE, "mt-ds-4 space-y-ds-3 p-[var(--density-panel-padding)]")}>{heroAppend}</div> : null}
    </>
  );
}

export type SectionHeaderProps = ComponentProps<"div"> & { title?: ReactNode; description?: ReactNode; actions?: ReactNode };
export function SectionHeader({ title, description, actions, className, ...props }: SectionHeaderProps) {
  if (!title && !description && !actions) return null;
  return <div className={cn("flex flex-col gap-ds-2 border-b border-card-border pb-ds-3 sm:flex-row sm:items-start sm:justify-between", className)} {...props}>
    <div className="min-w-0 space-y-ds-1">{title ? <h2 className="text-title text-foreground">{title}</h2> : null}{description ? <div className="text-caption text-muted">{description}</div> : null}</div>
    {actions ? <div className="flex shrink-0 flex-wrap items-center gap-ds-2">{actions}</div> : null}
  </div>;
}

export type PageSectionProps = ComponentProps<"section"> & Omit<SectionHeaderProps, "className">;
export function PageSection({ title, description, actions, children, className, ...props }: PageSectionProps) {
  return <section className={cn("min-w-0 space-y-ds-4", className)} {...props}><SectionHeader title={title} description={description} actions={actions} />{children}</section>;
}

export type EnterprisePageShellProps = PageHeaderProps & {
  hideHeader?: boolean; denseToolbar?: ReactNode; children: ReactNode; layout?: PageLayout;
  size?: PageSize; fullWidth?: boolean; pageClassName?: string;
};
export function EnterprisePageShell({ hideHeader, denseToolbar, children, layout = "detail", size, fullWidth, pageClassName, ...header }: EnterprisePageShellProps) {
  return <WorkspacePage layout={layout} size={size} fullWidth={fullWidth} className={cn("pt-0", pageClassName)}><PageStack>
    {!hideHeader ? <PageHeader {...header} /> : null}
    {denseToolbar ? <div className="border-b border-card-border pb-ds-4">{denseToolbar}</div> : null}
    <div className="min-h-0 min-w-0 flex-1">{children}</div>
  </PageStack></WorkspacePage>;
}
